<template>
    <v-card :class="componentName" row>
        <v-toolbar dark tabs color="secondary" height="12">
            <template v-slot:extension>
                <v-tabs
                    v-model="tab"
                    background-color="secondary"
                    ripple
                    slider-color="accent"
                    slider-size="3"
                    show-arrows
                    class="px-4"
                >
                    <v-tab href="#tab-1" :disabled="isAllProcessing">
                        <v-icon small class="pr-2"> fa-sign-in-alt </v-icon>
                        {{ $t('Login') }}
                    </v-tab>
                </v-tabs>
            </template>
        </v-toolbar>
        <v-progress-linear v-show="processing" ref="progress-bar" color="accent" indeterminate class="mt-0 mb-0" />
        <v-card-text>
            <v-tabs-items v-model="tab">
                <v-tab-item value="tab-1">
                    <login-form
                        :processing="processing"
                        :domain="domainName"
                        :multi-auth="isMultiAuth"
                        :mail-dialog-menu="mailDialogMenu"
                        :saml-enabled="samlEnabled"
                        @login="submitLogin"
                        @open-mail-dialog="showSendMailDialog"
                        @ldap-login="submitLdapLogin"
                        @sso="submitSso"
                    />
                </v-tab-item>
            </v-tabs-items>
        </v-card-text>
        <the-send-mail-dialog
            :is-show="isShowMailDialog"
            :menu-title="menuTitle"
            @cancel-dialog="isShowMailDialog = false"
            @send-mail="sendMail"
        />
    </v-card>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import { toRefs } from '@vueuse/core';
import { useAuthStore } from '../../../store/modules/auth/composables';
import { useNodeSamlStore } from '../../../store/modules/auth/node_saml/composables';
import { LoginForm } from '../../molecules';
import TheSendMailDialog from './TheSendMailDialog.vue';
import { useComponentName } from '../../../composables/ComponentName';
import { useRouter, useRoute } from 'vue-router/composables';
import ScreenUrl from '../../../router/url';

interface MailMenuEntry {
    id: number;
    title: string;
    name: string;
}

/*
 * properties
 */
interface Props {
    domainName?: string;
    samlEnabled?: boolean;
    isMultiAuth?: boolean;
    mailMenu?: Array<MailMenuEntry>;
    autoSamlLogin?: boolean;
}
const props = withDefaults(defineProps<Props>(), {
    domainName: '',
    samlEnabled: false,
    isMultiAuth: false,
    mailMenu: () => [],
    autoSamlLogin: false,
});
const { domainName, samlEnabled, isMultiAuth, mailMenu, autoSamlLogin } = toRefs(props);

/*
 * composables
 */
const {
    login,
    ldapLogin,
    processing,
    error,
    isAfterLogin,
    sso,
    multifactorLogin,
    ldapMultifactorLogin,
    sendUnlockMail,
    sendPasswordResetMail,
    sendUnconfirmedMail,
    amIAuthed,
} = useAuthStore();
const { setData } = useNodeSamlStore();
const { componentName } = useComponentName();
const router = useRouter();
const route = useRoute();

/*
 * getter,setters
 */

const tab = ref('tab-1');
const menu = ref(0);
const menuTitle = ref('');
const isShowMailDialog = ref(false);

const mailDialogMenu = computed(() => mailMenu.value);
const isAllProcessing = computed(() => processing.value);

/**
 * methods
 */
async function submitLogin(authData: { uid: string; password: string; onetimePassword: string }): Promise<void> {
    if (isMultiAuth.value) {
        await multifactorLogin(authData);
    } else {
        await login(authData);
    }
    if (!error.value && isAfterLogin.value) {
        await setData(nodeSamlParams());
        router.push(ScreenUrl.TOP);
    }
}
async function submitLdapLogin(authData: { uid: string; password: string; onetimePassword: string }): Promise<void> {
    if (isMultiAuth.value) {
        await ldapMultifactorLogin(authData);
    } else {
        await ldapLogin(authData);
    }
    if (!error.value && isAfterLogin.value) {
        await setData(nodeSamlParams());
        router.push(ScreenUrl.TOP);
    }
}
async function submitSso(): Promise<void> {
    await setData(nodeSamlParams());
    await sso();
}
function showSendMailDialog(menuNumber: number): void {
    menu.value = menuNumber;
    menuTitle.value = mailDialogMenu.value.find(menu => menu.id == menuNumber).title;
    isShowMailDialog.value = true;
}
async function sendMail(email: string) {
    if (menu.value === 1) {
        await sendUnlockMail(email);
    } else if (menu.value === 2) {
        await sendPasswordResetMail(email);
    } else {
        await sendUnconfirmedMail(email);
    }
    isShowMailDialog.value = false;
}

function nodeSamlParams() {
    return {
        id: (document.getElementsByName('node-saml-session')[0] as any).value,
        relay_state: (document.getElementsByName('relay-state')[0] as any).value,
        node_account_id: (document.getElementsByName('node-account-id')[0] as any).value,
    };
}

/*
 * lifecycle hook
 */
if (amIAuthed.value) {
    // 既に認証済みならログイン不要なので次の画面へ行く
    setData(nodeSamlParams()).then(() => {
        router.push(ScreenUrl.TOP);
    });
}
if (autoSamlLogin.value) {
    submitSso();
}
</script>
